import { useState, useEffect } from "react";
import { PartyPopper, Sparkles, User, ChevronDown, CalendarCheck, Users, Gift, ListChecks, Camera, CreditCard, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import heroImage from "@/assets/hero-party.jpg";
import FloatingDecorations from "@/components/party/FloatingDecorations";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import SEOHead from "@/components/SEOHead";
import { useLocalizedNavigate, useLocalizedPath } from "@/hooks/useLocalizedNavigate";
import { playCenters } from "@/lib/play-centers";

const LandingPage = () => {
  const navigate = useLocalizedNavigate();
  const { t, language } = useLanguage();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const policyPath = useLocalizedPath("/policy");
  
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsLoggedIn(!!session);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsLoggedIn(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const isSv = language === "sv";

  const texts = {
    smartPlanner: isSv ? "Smart Kalasplanerare" : "Smart Party Planner",
    heroLine1: isSv ? "Gratis Barnkalas Planering" : "Free Kids Party Planner",
    heroLine2: isSv ? "Datumröstning & Gästhantering" : "Date Voting & Guest Management",
    heroDesc: isSv
      ? "Skapa ett gratis konto, ladda upp din inbjudan, föreslå datum och låt gästerna rösta. Gästerna behöver inget konto — bara dela en länk!"
      : "Create a free account, upload your invitation, propose dates, and let your guests vote. Guests don't need an account — just share a link!",
    signIn: isSv ? "Logga in" : "Sign In",
    myParties: isSv ? "Mina Kalas" : "My Parties",
    footer: isSv ? "Gjord med 🎈 för kalasplanerare överallt" : "Made with 🎈 for party planners everywhere",
    // How it works
    howTitle: isSv ? "Så fungerar det" : "How It Works",
    howSubtitle: isSv ? "Tre enkla steg till ett stressfritt kalas" : "Three simple steps to a stress-free party",
    step1Title: isSv ? "Skapa inbjudan" : "Create Invitation",
    step1Desc: isSv ? "Välj en av våra färgglada mallar eller ladda upp din egen bild/PDF. Anpassa med barnets namn, ålder och kalastema." : "Choose a colorful template or upload your own image/PDF. Customize with the child's name, age, and party theme.",
    step2Title: isSv ? "Dela & låt gäster rösta" : "Share & Let Guests Vote",
    step2Desc: isSv ? "Skicka en länk via SMS, WhatsApp eller Messenger. Gästerna röstar på datum som passar och fyller i OSA med kostönskemål." : "Send a link via SMS, WhatsApp or Messenger. Guests vote on dates that work and RSVP with dietary requirements.",
    step3Title: isSv ? "Bekräfta & fira!" : "Confirm & Celebrate!",
    step3Desc: isSv ? "Se alla röster i realtid, boka det bästa datumet och planera aktiviteter, inköpslista och budget från din dashboard." : "See all votes in real-time, book the best date, and plan activities, shopping list, and budget from your dashboard.",
    // Features
    featTitle: isSv ? "Allt du behöver för kalaset" : "Everything You Need for the Party",
    featSubtitle: isSv ? "Kraftfulla verktyg som gör kalasplaneringen rolig istället för stressig" : "Powerful tools that make party planning fun instead of stressful",
    feat1Title: isSv ? "Datumröstning" : "Date Voting",
    feat1Desc: isSv ? "Föreslå 2-3 datum och låt gästerna rösta. Se resultaten i realtid i en tydlig matris." : "Propose 2-3 dates and let guests vote. See results in real-time in a clear matrix.",
    feat2Title: isSv ? "Gästhantering" : "Guest Management",
    feat2Desc: isSv ? "Importera kontakter, skicka inbjudningar via SMS/WhatsApp, spåra OSA och kostönskemål." : "Import contacts, send invitations via SMS/WhatsApp, track RSVPs and dietary requirements.",
    feat3Title: isSv ? "Önskelista" : "Wishlist",
    feat3Desc: isSv ? "Skapa en önskelista. Gäster reserverar presenter hemligt — inga dubbletter!" : "Create a wishlist. Guests secretly claim gifts — no duplicates!",
    feat4Title: isSv ? "Aktivitetsplanering" : "Activity Planning",
    feat4Desc: isSv ? "Planera kalas-schemat med aktiviteter, tider och varaktighet. Gästerna ser schemat." : "Plan the party schedule with activities, times, and duration. Guests see the timeline.",
    feat5Title: isSv ? "Fotodelning" : "Photo Sharing",
    feat5Desc: isSv ? "Gäster kan ladda upp bilder från kalaset till ett delat album." : "Guests can upload photos from the party to a shared album.",
    feat6Title: isSv ? "Budget & Inköpslista" : "Budget & Shopping List",
    feat6Desc: isSv ? "Håll koll på utgifter och dela inköpslistan med medplanerare." : "Track expenses and share the shopping list with co-planners.",
    // Social proof
    proofTitle: isSv ? "Älskat av föräldrar" : "Loved by Parents",
    proof1: isSv ? "Äntligen slipper jag skicka 20 separata SMS och hålla koll på vem som kan! Datumröstningen är genial." : "Finally I don't have to send 20 separate texts and track who can make it! The date voting is genius.",
    proof1Author: isSv ? "— Anna, mamma till Ella 6 år" : "— Anna, mother of Ella, age 6",
    proof2: isSv ? "Önskelistan med hemlig reservation är perfekt. Inga fler dubbelpresenter!" : "The wishlist with secret claiming is perfect. No more duplicate gifts!",
    proof2Author: isSv ? "— Marcus, pappa till Hugo 8 år" : "— Marcus, father of Hugo, age 8",
    proof3: isSv ? "Använde appen till mitt barns kalas på 10 minuter. Så enkelt!" : "Set up my kid's party in 10 minutes using the app. So easy!",
    proof3Author: isSv ? "— Sara, mamma till Wilma 4 år" : "— Sara, mother of Wilma, age 4",
    // FAQ
    faqTitle: isSv ? "Vanliga frågor" : "Frequently Asked Questions",
    faqSubtitle: isSv ? "Har du fler frågor? Testa appen — det är gratis!" : "Have more questions? Try the app — it's free!",
    // CTA
    ctaTitle: isSv ? "Redo att planera kalaset?" : "Ready to plan the party?",
    ctaDesc: isSv ? "Kom igång gratis på under 5 minuter. Skapa konto och börja planera!" : "Get started for free in under 5 minutes. Create an account and start planning!",
  };

  const faqs = isSv ? [
    { q: "Är PartyWhen gratis?", a: "Ja, helt gratis med alla funktioner. Du behöver inte betala någonting." },
    { q: "Behöver jag skapa konto?", a: "Ja, du skapar ett gratis konto via Google eller Apple för att planera kalas. Dina gäster behöver dock inget konto — de klickar bara på länken du delar." },
    { q: "Kan gäster rösta på datum?", a: "Ja! Du föreslår 2-3 datum och gästerna röstar Ja, Kanske eller Nej. Du ser resultatet direkt i din dashboard." },
    { q: "Hur fungerar önskelistan?", a: "Du lägger in presenter. Gästerna ser listan och kan hemligt reservera en present — så andra inte köper samma sak." },
    { q: "Kan jag bjuda in via SMS eller WhatsApp?", a: "Ja, du kan skicka inbjudan direkt via SMS, WhatsApp eller Messenger med ett klick." },
    { q: "Fungerar det på mobilen?", a: "Absolut! Appen är mobilanpassad för både dig som planerar och gästerna som svarar." },
    { q: "Kan flera vuxna administrera kalaset?", a: "Ja, du kan lägga till medadministratörer som får egna hemliga länkar till dashboarden." },
    { q: "Vad händer med mina uppgifter?", a: "Dina uppgifter sparas säkert på ditt konto. Du kan radera ditt konto och all data när som helst. Läs mer i vår integritetspolicy." },
  ] : [
    { q: "Is PartyWhen free?", a: "Yes, completely free with all features included. No hidden costs." },
    { q: "Do I need to create an account?", a: "Yes, you create a free account via Google or Apple to plan parties. Your guests don't need an account though — they just click the link you share." },
    { q: "Can guests vote on dates?", a: "Yes! Propose 2-3 dates and guests vote Yes, Maybe, or No. See results in real-time on your dashboard." },
    { q: "How does the wishlist work?", a: "You add gift ideas. Guests see the list and can secretly claim an item — so others won't buy the same thing." },
    { q: "Can I invite via SMS or WhatsApp?", a: "Yes, send invitations directly via SMS, WhatsApp, or Messenger with one click." },
    { q: "Does it work on mobile?", a: "Absolutely! The app is fully responsive for both planners and guests." },
    { q: "Can multiple people manage the party?", a: "Yes, you can add co-admins who get their own secret links to the dashboard." },
    { q: "What happens to my data?", a: "Your data is stored securely on your account. You can delete your account and all data at any time. Read more in our privacy policy." },
  ];

  const features = [
    { icon: CalendarCheck, title: texts.feat1Title, desc: texts.feat1Desc },
    { icon: Users, title: texts.feat2Title, desc: texts.feat2Desc },
    { icon: Gift, title: texts.feat3Title, desc: texts.feat3Desc },
    { icon: ListChecks, title: texts.feat4Title, desc: texts.feat4Desc },
    { icon: Camera, title: texts.feat5Title, desc: texts.feat5Desc },
    { icon: CreditCard, title: texts.feat6Title, desc: texts.feat6Desc },
  ];

  const steps = [
    { num: "1", emoji: "🎨", title: texts.step1Title, desc: texts.step1Desc },
    { num: "2", emoji: "📅", title: texts.step2Title, desc: texts.step2Desc },
    { num: "3", emoji: "🎉", title: texts.step3Title, desc: texts.step3Desc },
  ];

  const testimonials = [
    { text: texts.proof1, author: texts.proof1Author },
    { text: texts.proof2, author: texts.proof2Author },
    { text: texts.proof3, author: texts.proof3Author },
  ];

  return (
    <div className="min-h-screen bg-background relative">
      <SEOHead page="home" />
      <FloatingDecorations />
      
      {/* Top bar */}
      <div className="absolute top-4 left-4 right-4 z-20 flex items-center justify-between">
        <Link to={`/${language}/`} className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center shadow-button">
            <PartyPopper className="w-4 h-4 text-primary-foreground" />
          </div>
          <span className="font-display font-bold text-lg text-foreground group-hover:text-primary transition-colors">
            PartyWhen
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(isLoggedIn ? "/my-parties" : "/auth")}
            className="rounded-xl"
          >
            <User className="w-4 h-4 mr-1" />
            {isLoggedIn ? texts.myParties : texts.signIn}
          </Button>
        </div>
      </div>
      
      {/* Hero */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt={isSv ? "Barnkalas firande" : "Party celebration"} className="w-full h-full object-cover opacity-20" fetchPriority="high" width={1920} height={1080} decoding="async" />
          <div className="absolute inset-0 bg-gradient-hero" />
        </div>
        
        <div className="relative container mx-auto px-4 py-20 md:py-32 text-center">
          <div className="animate-bounce-in">
            <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary font-body">{texts.smartPlanner}</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold font-display text-foreground mb-4 leading-tight">
              {texts.heroLine1}
              <br />
              <span className="text-gradient-party">{texts.heroLine2}</span>
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground font-body max-w-2xl mx-auto mb-8">
              {texts.heroDesc}
            </p>
            
            <Button
              size="lg"
              onClick={() => navigate(isLoggedIn ? "/create" : "/auth?redirect=create")}
              className="text-lg px-8 py-6 rounded-full shadow-button font-display"
            >
              <PartyPopper className="w-5 h-5 mr-2" />
              {t.createParty}
            </Button>
          </div>
        </div>
      </header>

      {/* How it works */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-display text-foreground mb-3">{texts.howTitle}</h2>
          <p className="text-muted-foreground font-body text-lg max-w-xl mx-auto">{texts.howSubtitle}</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
          {steps.map((s, i) => (
            <div key={i} className="relative text-center">
              <div className="text-5xl mb-4 animate-float" style={{ animationDelay: `${i * 0.5}s` }}>
                {s.emoji}
              </div>
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground font-display font-bold text-sm mb-3">
                {s.num}
              </div>
              <h3 className="font-display text-lg font-semibold text-foreground mb-2">{s.title}</h3>
              <p className="text-muted-foreground font-body text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features grid */}
      <section className="bg-muted/30 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-foreground mb-3">{texts.featTitle}</h2>
            <p className="text-muted-foreground font-body text-lg max-w-xl mx-auto">{texts.featSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {features.map((f, i) => (
              <div
                key={i}
                className="bg-card rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-shadow duration-300"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 mb-4">
                  <f.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-display text-base font-semibold text-card-foreground mb-2">{f.title}</h3>
                <p className="text-muted-foreground font-body text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-display text-foreground mb-3">{texts.proofTitle}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {testimonials.map((t, i) => (
            <blockquote key={i} className="bg-card rounded-2xl p-6 shadow-card">
              <p className="text-foreground font-body text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
              <cite className="text-muted-foreground font-body text-xs not-italic">{t.author}</cite>
            </blockquote>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted/30 py-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-display text-foreground mb-3">{texts.faqTitle}</h2>
            <p className="text-muted-foreground font-body">{texts.faqSubtitle}</p>
          </div>
          <Accordion type="multiple" className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-card rounded-xl border-none shadow-card px-6">
                <AccordionTrigger className="font-display font-semibold text-foreground text-left py-4 hover:no-underline">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground font-body text-sm pb-4 leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Play Centers */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-display text-foreground mb-3">
            {isSv ? "Populära lekland" : "Popular Play Centers"}
          </h2>
          <p className="text-muted-foreground font-body text-lg max-w-xl mx-auto">
            {isSv ? "Boka kalas på dessa lekland med PartyWhen" : "Book parties at these play centers with PartyWhen"}
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {playCenters.map((center) => (
            <Link
              key={center.id}
              to={`/${language}/lekland/${center.id}`}
              className="bg-card rounded-2xl p-6 shadow-card hover:shadow-card-hover transition-shadow duration-300 block group"
            >
              <h3 className="font-display text-base font-semibold text-card-foreground mb-2 group-hover:text-primary transition-colors">
                🏰 {center.name}
              </h3>
              <div className="flex items-center gap-1 text-muted-foreground text-xs font-body mb-2">
                <MapPin className="w-3 h-3" />
                {center.address}
              </div>
              <p className="text-muted-foreground font-body text-sm leading-relaxed">
                {center.description[language]}
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="container mx-auto px-4 py-20 text-center">
        <div className="bg-primary/5 border-2 border-primary/20 rounded-3xl p-10 md:p-16 max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold font-display text-foreground mb-4">{texts.ctaTitle}</h2>
          <p className="text-muted-foreground font-body text-lg mb-8">{texts.ctaDesc}</p>
          <Button
            size="lg"
            onClick={() => navigate(isLoggedIn ? "/create" : "/auth?redirect=create")}
            className="text-lg px-8 py-6 rounded-full shadow-button font-display"
          >
            <PartyPopper className="w-5 h-5 mr-2" />
            {t.createParty}
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-center py-8 text-muted-foreground text-sm font-body border-t border-border space-y-2">
        <p>{texts.footer}</p>
        <p>
          <Link to={policyPath} className="text-xs hover:text-foreground transition-colors underline underline-offset-2">
            {isSv ? "Integritetspolicy & Cookiepolicy" : "Privacy Policy & Cookie Policy"}
          </Link>
        </p>
        <p className="text-xs">© {new Date().getFullYear()} PartyWhen</p>
      </footer>
    </div>
  );
};

export default LandingPage;
